/**
 * Desktop Navigation — interactive behavior.
 *
 * Manages dropdown menus, keyboard navigation (ARIA menubar pattern),
 * hover interactions, sticky mode, sliding indicator, and animations.
 *
 * Loaded via desktop-navigation.isml.
 */
'use strict';

export function initDesktopNavigation() {
    let FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';

    let MENU_CLASS = 'desktopNavigation__menu';
    let MENU_VISIBLE = 'desktopNavigation__menu--visible';
    let MENU_ANIMATED = 'desktopNavigation__menu--animated';
    let MENU_CLOSING = 'desktopNavigation__menu--closing';
    let INDICATOR_VISIBLE = 'desktopNavigation__indicator--visible';

    let nav = document.getElementById('desktop-navigation');
    if (!nav) return;

    let list = nav.querySelector('.desktopNavigation__list');
    let indicator = nav.querySelector('.desktopNavigation__indicator');
    let skipLink = nav.querySelector('.desktopNavigation__skipNavigation');
    if (!list) return;

    let listItems = list.querySelectorAll(
        ':scope > .desktopNavigation__listItem',
    );
    if (!listItems.length) return;

    // Build lookup maps from DOM
    let itemIds = [];
    let buttonsByItemId = {};
    let menusByItemId = {};
    let listItemsByItemId = {};
    let itemHasMenuMap = {};

    listItems.forEach((li) => {
        let id = li.getAttribute('id');

        if (!id) return;
        itemIds.push(id);
        listItemsByItemId[id] = li;
        buttonsByItemId[id] = li.querySelector(
            'button.desktopNavigation__item',
        );

        let menu = li.querySelector(`.${MENU_CLASS}`);
        if (menu) {
            menusByItemId[id] = menu;
            itemHasMenuMap[id] = true;
        } else {
            itemHasMenuMap[id] = false;
        }
    });

    // -------------------- State --------------------
    let openMenuId = null;
    let previousOpenMenuId = null;
    let isClosingMenuId = null;
    let stickyMenuId = null;

    // -------------------- Helpers --------------------

    /**
     * Gets all focusable elements within a menu container.
     * Queries the menu for interactive elements that can receive keyboard focus:
     * links with `href` and enabled buttons. Returns an empty array if the menu
     * element is undefined or contains no focusable elements.
     * @param {HTMLElement|undefined} menuEl - Menu element to search within
     * @returns {HTMLElement[]} Array of focusable elements in document order
     * @private
     */
    function _getFocusableElements(menuEl) {
        if (!menuEl) return [];

        return Array.prototype.slice.call(
            menuEl.querySelectorAll(FOCUSABLE_SELECTOR),
        );
    }

    /**
     * Calculates the next item index with circular wrapping.
     * @param {number} current - Current item index (0-based)
     * @param {number} total - Total number of items
     * @returns {number} Next index (wraps to 0 at end)
     * @private
     */
    function _getNextIndex(current, total) {
        return (current + 1) % total;
    }

    /**
     * Calculates the previous item index with circular wrapping.
     * @param {number} current - Current item index (0-based)
     * @param {number} total - Total number of items
     * @returns {number} Previous index (wraps to last at start)
     * @private
     */
    function _getPreviousIndex(current, total) {
        return current === 0 ? total - 1 : current - 1;
    }

    /**
     * Finds the index of a navigation item by its ID.
     * @param {string} itemId - Navigation item ID to look up
     * @returns {number} Index in `itemIds` array, or -1 if not found
     * @private
     */
    function _getItemIndex(itemId) {
        return itemIds.indexOf(itemId);
    }

    // -------------------- Indicator --------------------

    /**
     * Updates the sliding indicator position based on the given DesktopNavItem.
     * Calculates position using the DesktopNavItem button's bounding rect relative to
     * the list container. Dynamically reads `padding-inline-start` from computed
     * styles to account for responsive padding changes, then sets CSS custom
     * properties `--indicator-left` and `--indicator-width` on the indicator element.
     * @param {string} itemId - ID of the DesktopNavItem to position indicator under
     * @private
     */
    function _updateIndicatorPosition(itemId) {
        if (!indicator) return;

        let button = buttonsByItemId[itemId];
        if (!button) return;

        let listRect = list.getBoundingClientRect();
        let buttonRect = button.getBoundingClientRect();
        let computedStyle = getComputedStyle(button);
        let padding = parseFloat(computedStyle.paddingInlineStart) || 16;
        let left = buttonRect.left - listRect.left + padding;
        let width = buttonRect.width - padding * 2;

        indicator.style.setProperty('--indicator-left', `${left}px`);
        indicator.style.setProperty('--indicator-width', `${width}px`);
    }

    /**
     * Shows the sliding indicator by adding its visible class.
     * @private
     */
    function _showIndicator() {
        if (indicator) indicator.classList.add(INDICATOR_VISIBLE);
    }

    /**
     * Hides the sliding indicator by removing its visible class.
     * @private
     */
    function _hideIndicator() {
        if (indicator) indicator.classList.remove(INDICATOR_VISIBLE);
    }

    // -------------------- Menu Visibility --------------------

    /**
     * Shows a menu dropdown by adding visibility class and updating ARIA state.
     * Adds `--visible` class, sets `aria-hidden="false"`, removes `inert`,
     * and sets `aria-expanded="true"` on the associated button.
     * @param {string} itemId - ID of the navigation item whose menu to show
     * @private
     */
    function _showMenu(itemId) {
        let menu = menusByItemId[itemId];
        if (!menu) return;

        if (isClosingMenuId === itemId) {
            menu.classList.remove(MENU_CLOSING);
            isClosingMenuId = null;
        }

        menu.classList.add(MENU_VISIBLE);
        menu.setAttribute('aria-hidden', 'false');
        menu.removeAttribute('inert');

        let button = buttonsByItemId[itemId];
        if (button) button.setAttribute('aria-expanded', 'true');
    }

    /**
     * Hides a menu dropdown by removing visibility class and updating ARIA state.
     * When `animate` is true, applies the closing animation class (`--closing`)
     * and defers `aria-hidden`/`inert` until `animationend`. When false, immediately
     * sets `aria-hidden="true"` and `inert`. Always sets `aria-expanded="false"`.
     * @param {string} itemId - ID of the navigation item whose menu to hide
     * @param {boolean} animate - Whether to apply the closing (slideIn reverse) animation
     * @private
     */
    function _hideMenu(itemId, animate) {
        let menu = menusByItemId[itemId];
        if (!menu) return;

        menu.classList.remove(MENU_VISIBLE);
        menu.classList.remove(MENU_ANIMATED);

        if (animate) {
            menu.classList.add(MENU_CLOSING);
            isClosingMenuId = itemId;
        } else {
            menu.setAttribute('aria-hidden', 'true');
            menu.setAttribute('inert', '');
        }

        let button = buttonsByItemId[itemId];
        if (button) button.setAttribute('aria-expanded', 'false');
    }

    // -------------------- Menu Open/Close Logic --------------------

    /**
     * Opens a menu and updates animation/indicator state.
     * Hides the previously open menu (without animation — opacity transition
     * handled via CSS). Tracks whether this is the first open (no previous menu)
     * to apply `slideIn` animation, or a menu switch (opacity transition only).
     * In sticky mode, hover also updates the sticky menu ID.
     * @param {string} itemId - ID of the navigation item whose menu to open
     * @private
     */
    function _openMenu(itemId) {
        if (openMenuId === itemId) return;

        if (openMenuId === null) _closeSearchIfOpen();

        // Hide previously open menu (no animation on switch, opacity transition via CSS)
        if (openMenuId && openMenuId !== itemId) {
            _hideMenu(openMenuId, false);
        }

        let isFirstOpen = previousOpenMenuId === null && openMenuId === null;

        previousOpenMenuId = openMenuId;
        openMenuId = itemId;

        _showMenu(itemId);
        _updateIndicatorPosition(itemId);
        _showIndicator();

        // Apply slideIn animation only on first open (no previous menu)
        let menu = menusByItemId[itemId];
        if (menu && isFirstOpen) {
            menu.classList.add(MENU_ANIMATED);
        }
    }

    /**
     * Closes the currently open menu with optional animation.
     * Triggers the `slideIn` reverse animation when `animate` is true.
     * Hides the indicator and updates internal state.
     * @param {boolean} animate - Whether to apply closing animation
     * @private
     */
    function _closeMenu(animate) {
        if (!openMenuId) return;

        let closingId = openMenuId;
        previousOpenMenuId = openMenuId;
        openMenuId = null;

        _hideIndicator();
        _hideMenu(closingId, animate);
    }

    /**
     * Closes a menu with animation and returns focus to its DesktopNavItem button.
     * Disables sticky mode, triggers closing animation, and moves keyboard
     * focus back to the navigation button. Used by Escape key and
     * Enter/Space toggle handlers.
     * @param {string} itemId - ID of the navigation item whose menu to close
     * @private
     */
    function _closeMenuAndFocus(itemId) {
        stickyMenuId = null;
        previousOpenMenuId = openMenuId;
        openMenuId = null;

        _hideIndicator();
        _hideMenu(itemId, true);

        let button = buttonsByItemId[itemId];
        if (button) button.focus();
    }

    /**
     * Opens a menu, enables sticky mode, and focuses a menu item.
     * Enables sticky mode for the given item. If the menu isn't already open,
     * resets `previousOpenMenuId` to null (so `slideIn` animation applies on
     * next open) and shows the menu. Removes `inert` imperatively before
     * focusing so the focus call succeeds before the DOM fully updates.
     * Focuses either the first or last focusable element based on `focusFirst`.
     * @param {string} itemId - ID of the navigation item whose menu to open
     * @param {boolean} focusFirst - If true, focus first focusable; if false, focus last
     * @private
     */
    function _openMenuAndFocus(itemId, focusFirst) {
        stickyMenuId = itemId;

        let isAlreadyOpen = openMenuId === itemId;

        if (!isAlreadyOpen) {
            if (openMenuId === null) _closeSearchIfOpen();
            if (openMenuId) _hideMenu(openMenuId, false);
            previousOpenMenuId = null;
            openMenuId = itemId;
            _showMenu(itemId);
            _updateIndicatorPosition(itemId);
            _showIndicator();
        }

        let button = buttonsByItemId[itemId];
        if (button) button.focus();

        let menu = menusByItemId[itemId];
        if (!menu) return;

        menu.removeAttribute('inert');

        let focusables = _getFocusableElements(menu);
        if (focusables.length === 0) return;

        let target = focusFirst
            ? focusables[0]
            : focusables[focusables.length - 1];
        if (target) target.focus();
    }

    /**
     * Navigates to a menu item and optionally opens its submenu.
     * Handles horizontal ArrowLeft/ArrowRight navigation between menu items.
     * If `openSubmenu` is true, opens the target's menu; otherwise closes
     * any open menu. Moves keyboard focus to the target DesktopNavItem button.
     * @param {string} targetItemId - ID of the target navigation item
     * @param {boolean} openSubmenu - Whether to open the target item's submenu
     * @private
     */
    function _navigateToItem(targetItemId, openSubmenu) {
        if (!openSubmenu && openMenuId) {
            _hideMenu(openMenuId, false);

            openMenuId = null;
            stickyMenuId = null;

            _hideIndicator();
        }

        if (openSubmenu) {
            if (openMenuId && openMenuId !== targetItemId) {
                _hideMenu(openMenuId, false);
            }

            openMenuId = targetItemId;
            stickyMenuId = null;

            _showMenu(targetItemId);
            _updateIndicatorPosition(targetItemId);
            _showIndicator();
        }

        let button = buttonsByItemId[targetItemId];
        if (button) button.focus();
    }

    // -------------------- Boundary Detection --------------------

    /**
     * Determines whether `relatedTarget` indicates a genuine exit from the navigation list.
     * Shared boundary-detection logic used by both `handleListMouseOut` and
     * `handleListFocusOut`. Returns true when the mouse or focus has genuinely
     * left the navigation, filtering out spurious events caused by:
     * - No menu currently open (nothing to close)
     * - Null relatedTarget (DOM mutations, window transitions)
     * - Movement to another element within the navigation component
     * - Compositor layer changes (`backdrop-filter`) that momentarily report
     * `<html>` or `<body>` as relatedTarget
     * @param {EventTarget|null} relatedTarget - The event's relatedTarget value
     * @returns {boolean} True if the event represents a genuine exit
     * @private
     */
    function _isGenuineListExit(relatedTarget) {
        if (openMenuId === null) return false;
        if (!relatedTarget) return false;
        if (!(relatedTarget instanceof Node)) return true;
        if (nav.contains(relatedTarget)) return false;
        if (
            relatedTarget === document.documentElement ||
            relatedTarget === document.body
        ) {
            return false;
        }
        return true;
    }

    /**
     * Closes the header search flyout when it is currently open.
     * @private
     */
    function _closeSearchIfOpen() {
        let searchTriggers = document.querySelectorAll('[data-search-trigger]');
        if (!searchTriggers.length) return;

        for (let i = 0; i < searchTriggers.length; i++) {
            let searchToggle = searchTriggers[i].toggle;
            if (searchToggle && searchToggle.isSet) {
                searchToggle.unset();
                break;
            }
        }
    }

    /**
     * Closes the currently open menu and disables sticky mode.
     * Reacts to external triggers (e.g. the header search flyout opening) that
     * require the navigation to close regardless of whether it was opened via
     * hover or keyboard. Closing uses the slide-out animation and resets
     * sticky state so subsequent interactions start clean.
     * @private
     */
    function _closeNavigationFromExternalTrigger() {
        if (!openMenuId) return;

        stickyMenuId = null;
        previousOpenMenuId = openMenuId;
        let closingId = openMenuId;
        openMenuId = null;

        _hideIndicator();
        _hideMenu(closingId, true);
    }

    // -------------------- Event Handlers --------------------

    /**
     * Handles mouse enter on a navigation list item — opens its submenu.
     * Opens the submenu when the user hovers over a navigation item.
     * In sticky mode, also updates the sticky menu ID to follow hover.
     * No-ops if the item has no menu.
     * @param {string} itemId - ID of the navigation item being hovered
     * @private
     */
    function _handleMenuEnter(itemId) {
        if (!itemHasMenuMap[itemId]) return;

        _openMenu(itemId);

        if (stickyMenuId !== null) {
            stickyMenuId = itemId;
        }
    }

    /**
     * Handles mouse leaving the navigation list via mouseout on `<ul>`.
     * Uses bubbling `mouseout` (rather than non-bubbling `mouseleave`) because
     * it provides reliable `relatedTarget` values. Delegates to
     * `isGenuineListExit` for boundary detection, then closes the menu with
     * animation if the mouse has genuinely left. Skipped in sticky mode.
     * @param {MouseEvent} e - Mouse event bubbled up to the list element
     * @private
     */
    function _handleListMouseOut(e) {
        if (stickyMenuId !== null) return;

        if (_isGenuineListExit(e.relatedTarget)) {
            _closeMenu(true);
        }
    }

    /**
     * Keyboard-accessible complement to `handleListMouseOut`.
     * Ensures keyboard users get equivalent behavior when focus leaves the
     * navigation list. Delegates to `isGenuineListExit` for boundary detection.
     * Skipped in sticky mode.
     * @param {FocusEvent} e - Focus event bubbled up to the list element
     * @private
     */
    function _handleListFocusOut(e) {
        if (stickyMenuId !== null) return;

        if (_isGenuineListExit(e.relatedTarget)) {
            _closeMenu(true);
        }
    }

    /**
     * Handles focus on a DesktopNavItem button — opens its submenu via keyboard navigation.
     * Opens the submenu when a navigation button receives keyboard focus (e.g.
     * via Tab). If the focused item has no menu and a menu is currently open
     * (without sticky mode), closes the open menu with animation. This is needed
     * because `handleMenuFocusOut` skips closing when focus stays within the
     * navigation component (to preserve opacity transitions).
     * @param {string} itemId - ID of the navigation item receiving focus
     * @private
     */
    function _handleNavItemFocus(itemId) {
        if (itemHasMenuMap[itemId]) {
            _openMenu(itemId);
        } else if (openMenuId !== null && stickyMenuId === null) {
            _closeMenu(true);
        }
    }

    /**
     * Handles click on a DesktopNavItem button — toggles sticky mode.
     * Toggles sticky mode when the user clicks on a navigation item:
     * - Clicking the currently sticky menu disables sticky and closes with animation
     * - Clicking a different item (or no sticky active) enables sticky and opens
     * @param {string} itemId - ID of the clicked navigation item
     * @private
     */
    function _handleNavItemClick(itemId) {
        if (!itemHasMenuMap[itemId]) return;

        let isClosing = stickyMenuId === itemId;

        if (isClosing) {
            stickyMenuId = null;
            previousOpenMenuId = openMenuId;
            openMenuId = null;

            _hideIndicator();
            _hideMenu(itemId, true);
        } else {
            stickyMenuId = itemId;

            _openMenu(itemId);
        }
    }

    /**
     * Handles keyboard events on DesktopNavItem buttons (ARIA menubar pattern).
     * Processes keyboard interactions when focus is on a navigation button:
     * **Menu-related keys** (only when item has menu):
     * - Escape: Close open menu
     * - Enter/Space: Toggle menu visibility and sticky mode
     * - ArrowDown: Open menu and focus first item
     * - ArrowUp: Open menu and focus last item
     * **Horizontal navigation** (always):
     * - ArrowRight: Navigate to next item (circular)
     * - ArrowLeft: Navigate to previous item (circular)
     * @param {KeyboardEvent} e - Keyboard event from navigation button
     * @param {string} itemId - ID of the current navigation item
     * @private
     */
    function _handleNavItemKeyDown(e, itemId) {
        let currentIndex = _getItemIndex(itemId);
        if (currentIndex === -1) return;

        let hasMenu = itemHasMenuMap[itemId];

        // Menu-related keys (only if item has menu)
        if (hasMenu) {
            let isMenuOpen = openMenuId === itemId;

            if (e.key === 'Escape' && isMenuOpen) {
                e.preventDefault();
                _closeMenuAndFocus(itemId);
                return;
            }

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (isMenuOpen) {
                    _closeMenuAndFocus(itemId);
                } else {
                    _openMenuAndFocus(itemId, true);
                }
                return;
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                _openMenuAndFocus(itemId, true);
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                _openMenuAndFocus(itemId, false);
                return;
            }
        }

        // Horizontal navigation
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            let targetIndex =
                e.key === 'ArrowRight'
                    ? _getNextIndex(currentIndex, itemIds.length)
                    : _getPreviousIndex(currentIndex, itemIds.length);

            let targetId = itemIds[targetIndex];
            if (targetId) {
                e.preventDefault();
                _navigateToItem(targetId, itemHasMenuMap[targetId]);
            }
        }
    }

    /**
     * Handles keyboard events inside menu dropdowns (capture phase).
     * Implements ARIA menubar keyboard navigation for menu content:
     * **Close menu:**
     * - Escape: Close menu and return focus to DesktopNavItem button
     * **Vertical navigation (within menu):**
     * - ArrowDown: Navigate to next focusable element (circular)
     * - ArrowUp: Navigate to previous focusable element (circular)
     * **Horizontal navigation (between menus):**
     * - ArrowRight: Close current, open next item's menu, focus first element
     * - ArrowLeft: Close current, open previous item's menu, focus first element
     * Attached via capture phase (`addEventListener(..., true)`) to intercept
     * keyboard events before they bubble to parent elements.
     * @param {KeyboardEvent} e - Keyboard event from menu content
     * @param {string} itemId - ID of the navigation item that owns this menu
     * @private
     */
    function _handleMenuKeyDown(e, itemId) {
        let currentIndex = _getItemIndex(itemId);
        if (currentIndex === -1) return;

        let handledKeys = [
            'Escape',
            'ArrowDown',
            'ArrowUp',
            'ArrowRight',
            'ArrowLeft',
        ];
        if (handledKeys.indexOf(e.key) === -1) return;

        e.preventDefault();

        if (e.key === 'Escape') {
            _closeMenuAndFocus(itemId);
            return;
        }

        let menu = menusByItemId[itemId];
        let focusables = _getFocusableElements(menu);

        // Vertical navigation within menu
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (focusables.length === 0) return;

            let currentFocusIndex = -1;

            for (let i = 0; i < focusables.length; i++) {
                if (focusables[i] === document.activeElement) {
                    currentFocusIndex = i;
                    break;
                }
            }

            let nextFocusIndex;
            if (e.key === 'ArrowDown') {
                nextFocusIndex = (currentFocusIndex + 1) % focusables.length;
            } else {
                nextFocusIndex =
                    currentFocusIndex <= 0
                        ? focusables.length - 1
                        : currentFocusIndex - 1;
            }
            if (focusables[nextFocusIndex]) focusables[nextFocusIndex].focus();
            return;
        }

        // Horizontal navigation between menus
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            let targetIndex =
                e.key === 'ArrowRight'
                    ? _getNextIndex(currentIndex, itemIds.length)
                    : _getPreviousIndex(currentIndex, itemIds.length);

            let targetId = itemIds[targetIndex];
            if (targetId) {
                if (itemHasMenuMap[targetId]) {
                    _openMenuAndFocus(targetId, true);
                } else {
                    _navigateToItem(targetId, false);
                }
            }
        }
    }

    /**
     * Handles focus leaving a menu container.
     * Determines whether focus has truly left the menu item boundaries or just
     * moved to another element within the same navigation. Only closes the menu
     * if focus has moved completely outside the navigation component.
     * Focus boundary detection:
     * 1. Within same menu item (menu content or parent list item) — ignore
     * 2. Within navigation component (another DesktopNavItem button) — ignore,
     * because `handleNavItemFocus` handles the menu switch with proper
     * `previousOpenMenuId` tracking for smooth opacity transitions
     * 3. Outside navigation — close menu
     * @param {FocusEvent} e - Focus event from menu container
     * @param {string} itemId - ID of the navigation item that owns the menu
     * @private
     */
    function _handleMenuFocusOut(e, itemId) {
        if (stickyMenuId !== null) return;

        let relatedTarget = e.relatedTarget;

        if (relatedTarget instanceof Node) {
            let menu = menusByItemId[itemId];
            let listItem = listItemsByItemId[itemId];

            if (menu && menu.contains(relatedTarget)) return;
            if (listItem && listItem.contains(relatedTarget)) return;
            if (nav.contains(relatedTarget)) return;
        }

        if (openMenuId) {
            _hideMenu(openMenuId, false);

            openMenuId = null;

            _hideIndicator();
        }
    }

    /**
     * Handles focus leaving the navigation component — closes menu and disables sticky mode.
     * When focus moves outside the navigation component (e.g. user tabs away or
     * clicks elsewhere), closes any open menu with animation and disables sticky
     * mode. Only active when sticky mode is engaged. Uses `relatedTarget` to
     * determine if focus moved outside the navigation.
     * @param {FocusEvent} e - Focus event from the navigation root element
     * @private
     */
    function _handleNavigationFocusOut(e) {
        if (stickyMenuId === null) return;

        let relatedTarget = e.relatedTarget;
        if (!relatedTarget || !nav.contains(relatedTarget)) {
            stickyMenuId = null;

            if (openMenuId) {
                previousOpenMenuId = openMenuId;
                _hideMenu(openMenuId, true);
                openMenuId = null;
                _hideIndicator();
            }
        }
    }

    /**
     * Cleans up state after menu animation ends.
     * Handles two animation scenarios:
     * - **Closing animation ended** (`isClosingMenuId` matches): removes the
     * `--closing` class, sets `aria-hidden="true"` and `inert`, resets
     * `isClosingMenuId` and `previousOpenMenuId`
     * - **Opening slideIn animation ended** (`openMenuId` matches and no previous):
     * removes the `--animated` class and sets `previousOpenMenuId` so subsequent
     * menu switches use opacity transition instead of slideIn
     * @param {AnimationEvent} e - Animation event from menu element
     * @param {string} itemId - ID of the navigation item whose animation ended
     * @private
     */
    function _handleAnimationEnd(e, itemId) {
        let menu = menusByItemId[itemId];
        if (!menu) return;

        if (isClosingMenuId === itemId) {
            menu.classList.remove(MENU_CLOSING);
            menu.setAttribute('aria-hidden', 'true');
            menu.setAttribute('inert', '');
            isClosingMenuId = null;
            previousOpenMenuId = null;
        } else if (openMenuId === itemId && previousOpenMenuId === null) {
            menu.classList.remove(MENU_ANIMATED);
            previousOpenMenuId = itemId;
        }
    }

    // -------------------- Attach Event Listeners --------------------
    // Navigation focus out
    nav.addEventListener('focusout', _handleNavigationFocusOut);

    // Close navigation when the header search flyout opens.
    // The search trigger lives outside `nav#desktop-navigation`, so navigation
    // focus/mouse handlers do not fire when the user opens search via mouse
    // hover + keyboard. Born toggle dispatches a bubbling `toggle:afterSet`
    // event on the trigger element, allowing a document-level listener to
    // react to the search opening regardless of the navigation's internal state.
    document.addEventListener('toggle:afterSet', (e) => {
        let target = e.target;
        if (!(target instanceof Element)) return;
        if (!target.matches('[data-search-trigger]')) return;
        _closeNavigationFromExternalTrigger();
    });

    // Skip link focus closes menu
    if (skipLink) {
        skipLink.addEventListener('focus', () => {
            if (stickyMenuId !== null) return;
            _closeMenu(true);
        });
    }

    // List mouse out and focus out
    list.addEventListener('mouseout', _handleListMouseOut);
    list.addEventListener('focusout', _handleListFocusOut);

    // Per-item listeners
    itemIds.forEach((itemId) => {
        let li = listItemsByItemId[itemId];
        let button = buttonsByItemId[itemId];
        let menu = menusByItemId[itemId];

        if (li) {
            li.addEventListener('mouseenter', () => {
                _handleMenuEnter(itemId);
            });
        }

        if (button) {
            button.addEventListener('focus', () => {
                _handleNavItemFocus(itemId);
            });

            button.addEventListener('click', () => {
                _handleNavItemClick(itemId);
            });

            button.addEventListener('keydown', (e) => {
                _handleNavItemKeyDown(e, itemId);
            });
        }

        if (menu) {
            // Capture phase for menu keydown (mirrors onKeyDownCapture in React)
            menu.addEventListener(
                'keydown',
                (e) => {
                    _handleMenuKeyDown(e, itemId);
                },
                true,
            );

            menu.addEventListener('focusout', (e) => {
                _handleMenuFocusOut(e, itemId);
            });

            // Mouse enter on menu (keeps menu open when hovering menu content)
            menu.addEventListener('mouseenter', () => {
                _handleMenuEnter(itemId);
            });

            // Mouse leave from menu
            menu.addEventListener('mouseleave', (e) => {
                let relatedTarget = e.relatedTarget;
                if (relatedTarget && relatedTarget instanceof Node) {
                    if (li && li.contains(relatedTarget)) return;
                    if (nav.contains(relatedTarget)) return;
                }
                if (stickyMenuId !== null) return;
                _closeMenu(true);
            });

            menu.addEventListener('animationend', (e) => {
                _handleAnimationEnd(e, itemId);
            });
        }
    });
}
